import { api } from '@/lib/api';
import { Calendar, ChevronRight, Clock, MapPin, Ticket } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface PublicEvent {
  id: string;
  name: string;
  description?: string;
  event_date: string;
  venue_name: string;
  venue_address?: string;
  venue_image_url?: string;
  created_at: string;
  event_start_time?: string;
  event_end_time?: string;
}

export default function EventsListPage() {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const cached = sessionStorage.getItem('eventflow_public_events_v1');
        if (cached) {
          const parsed = JSON.parse(cached) as PublicEvent[];
          if (Array.isArray(parsed) && parsed.length >= 0) {
            setEvents(parsed);
            setIsLoading(false);
            return;
          }
        }

        setIsLoading(true);
        const data = await api.get<PublicEvent[]>('/events/public');
        setEvents(data);
        sessionStorage.setItem('eventflow_public_events_v1', JSON.stringify(data));
      } catch (err) {
        console.error('[EventsListPage] Error fetching events:', err);
        setError('Error al cargar eventos');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando eventos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
          Eventos Disponibles
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Encuentra tu evento favorito y compra tus entradas
        </p>
      </div>

      {/* Events Grid */}
      {events.length === 0 ? (
        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-lg">
          <Ticket className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">No hay eventos disponibles</h3>
          <p className="mt-1 text-gray-500">Vuelve pronto para ver nuevos eventos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {events.map((event) => (
            <Link
              key={event.id}
              to={`/eventos/${event.id}`}
              className="card hover:shadow-lg transition-shadow duration-200 cursor-pointer group"
            >
              {/* Event Image */}
              <div className="mb-3 sm:mb-4 aspect-video bg-gray-200 rounded-lg overflow-hidden">
                {event.venue_image_url ? (
                  <img
                    src={event.venue_image_url}
                    alt={event.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-slate-100">
                    <Ticket className="h-16 w-16 text-slate-300" />
                  </div>
                )}
              </div>

              {/* Event Info */}
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-1">
                  {event.name}
                </h2>

                {event.description && (
                  <p className="text-gray-600 text-xs sm:text-sm mb-2 sm:mb-3 line-clamp-2">
                    {event.description}
                  </p>
                )}

                {/* Date */}
                <div className="flex items-start text-gray-700 mb-2">
                  <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-xs sm:text-sm font-medium capitalize">
                    {formatDate(event.event_date)}
                  </span>
                </div>

                {/* Time & Duration */}
                {event.event_start_time && event.event_end_time && (
                  <div className="flex items-start text-gray-700 mb-2">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex flex-wrap gap-1 text-xs sm:text-sm font-medium">
                      <span>{event.event_start_time.slice(0, 5)} - {event.event_end_time.slice(0, 5)}</span>
                      {(() => {
                        const [startH, startM] = event.event_start_time!.split(':').map(Number);
                        const [endH, endM] = event.event_end_time!.split(':').map(Number);
                        const diff = (endH * 60 + endM) - (startH * 60 + startM);
                        if (diff > 0) {
                          const h = Math.floor(diff / 60);
                          const m = diff % 60;
                          return <span className="text-gray-500 font-normal">({h}h {m}min)</span>;
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                )}

                {/* Venue */}
                <div className="flex items-start text-gray-700">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm min-w-0">
                    <p className="font-medium truncate">{event.venue_name}</p>
                    {event.venue_address && (
                      <p className="text-gray-500 text-xs truncate">{event.venue_address}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
                <span className="text-blue-600 font-semibold group-hover:text-blue-700 flex items-center text-sm sm:text-base">
                  Ver asientos
                  <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
